import * as React from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';

import { useState, useEffect } from 'react';

import '../../../Css/Login/Login.css';
import { Skeleton } from '@mui/material';
import {auth, generateTwoFactorCode, listAdminModulesActions as listAdminModulesActionsRequest} from "../../../Requests";
import {generateCsrfToken} from "../../../Requests/request";
import {generateRandomString, maskEmail} from "../../../Helpers";
import {listAdminModulesActions as listAdminModulesActionsStorage, rememberCredentials as rememberCredentialsData} from "../../../Storages";

interface ICredentials {
    email: string;
    password: string;
};

interface ILoginSideProps {
    setTwoFactor: React.Dispatch<React.SetStateAction<boolean>>;
    twoFactor: boolean;
}

const maskCredentials = (credentials: ICredentials): ICredentials => {
    if(!credentials.email || !credentials.password) {
        return {
            email: '',
            password: ''
        }
    }

    return {
        email: maskEmail(credentials.email),
        password: generateRandomString(21)
    }
}

const LoginSide: React.FC<ILoginSideProps> = ({setTwoFactor, twoFactor}) => {

    const {email: emailSaved = '', password: passwordSaved = ''} = rememberCredentialsData.get();
    const {email: emailMasked = '', password: passwordMasked = ''} = maskCredentials({email: emailSaved, password: passwordSaved});
    const [credentialsSaved, setCredentialsSaved] = useState<ICredentials>({email: emailSaved, password: passwordSaved});
    const [credentialsMasked, setCredentialsMasked] = useState<ICredentials>({email: emailMasked, password: passwordMasked});
    const [credentials, setCredentials] = useState<ICredentials>({ email: emailMasked, password: passwordMasked});
    const [isLoadingVerify, setIsLoadingVerify] = useState<boolean>(false);
    const [loginMessage, setLoginMessage] = useState<{message: string, status: "error" | "success" | "info" | "warning"}>({message: '', status: 'warning'});
    const [rememberCredentials, setRememberCredentials] = useState<boolean>((!!emailSaved && !!passwordSaved));

    useEffect(() => {
        generateCsrfToken();
    }, []);

    useEffect(() => {
        if(credentials.email !== credentialsMasked.email && credentials.password === credentialsMasked.password && credentials.password !== '') {
            // setCredentials(credentialsMasked);
            setCredentials({ email: '', password: ''});
        }
    }, [credentials, credentialsMasked]);

    const handleRememberCredentialsChange = () => {
        setRememberCredentials(n => {
            if(!credentials.email || !credentials.password) {
                return false;
            }

            if(n) {
                localStorage.removeItem('aplicationtbkpay');
                setCredentials({ email: '', password: ''});
                setCredentialsSaved({ email: '', password: ''});
                setCredentialsMasked({ email: '', password: ''});
                return !n;
            }

            if(!!credentials.email && !!credentials.password) {
                rememberCredentialsData.save({
                    email: credentials.email,
                    password: credentials.password
                });


                const {email: emailSavedInternal = '', password: passwordSavedInternal = ''} = credentials;
                const {email: emailMaskedInternal = '', password: passwordMaskedInternal = ''} = maskCredentials(credentials);

                setCredentialsSaved({email: emailSavedInternal, password: passwordSavedInternal});
                setCredentials({email: emailMaskedInternal, password: passwordMaskedInternal});
                setCredentialsMasked({email: emailMaskedInternal, password: passwordMaskedInternal});
            }

            return !n;
        });
    };

    const getCredentials = (): ICredentials => {
        if(!credentials.email || !credentials.password) {
            return credentials;
        }

        if(!credentialsSaved.email || !credentialsSaved.password) {
            return credentials;
        }

        if(credentials.email === credentialsMasked.email && credentials.password === credentialsMasked.password) {
            return credentialsSaved;
        }

        return credentials;
    }

    const handleSubmit = async (event:any) => {
        event.preventDefault();

        setIsLoadingVerify(true);
        setLoginMessage({message: '', status: 'warning'});
        setTwoFactor(false);

        const credentialsSend = getCredentials();

        const {
            status = "error",
            message = 'Houve erro na tentativa de login',
            email = '',
            access_token = '',
            access_profile_id
        } = await auth.post(credentialsSend);

        setLoginMessage({message, status});

        if(status !== "success") {
            setIsLoadingVerify(false);
            const {email: emailSaved = '', password: passwordSaved = ''} = rememberCredentialsData.get();
            if(credentialsSend.email === emailSaved && credentialsSend.password === passwordSaved) {
                rememberCredentialsData.remove();
                setCredentials({ email: '', password: ''});
                setCredentialsSaved({ email: '', password: ''});
                setCredentialsMasked({ email: '', password: ''});
                setRememberCredentials(false);
            }
            return;
        }

        const twoFactor = await generateTwoFactorCode.addHeader({'Authorization': 'Bearer ' + access_token}).get(email);

        if (twoFactor?.error) { //validação extremamente fraca corrigir o backend para que deixe de retornar uma string
            setLoginMessage({message: twoFactor?.error, status: 'error'});
            setIsLoadingVerify(false);
            return;
        }

        if (twoFactor?.status === "error") { //validação extremamente fraca corrigir o backend para que deixe de retornar uma string
            setLoginMessage({message: twoFactor.message, status: 'error'});
            setIsLoadingVerify(false);
            return;
        }

        listAdminModulesActionsStorage.save(
            await listAdminModulesActionsRequest
                .addHeader({'Authorization': 'Bearer ' + access_token})
                .get(access_profile_id + '')
        );

        setTwoFactor(true);
        setIsLoadingVerify(false);
    };

    return (

        <Grid container component="main" sx={{ height: '100vh' }}>
            <CssBaseline />
            <Grid item xs={12} mt={14} >
                <Box
                    sx={{
                        my: 8,
                        mx: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >

                    <img alt="TribuNex - Sistema de Gestão Tributária" src="img/logo.png" className="loginImg" />

                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 4 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Seu e-mail"
                            name="email"
                            autoComplete="off"
                            autoFocus
                            value={credentials.email}
                            onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                            disabled={isLoadingVerify}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Sua senha"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={credentials.password}
                            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                            disabled={isLoadingVerify || emailMasked === credentials.email}
                        />
                        <FormControlLabel
                            name="rememberCredentials"
                            control={<Checkbox checked={rememberCredentials} color="primary" />}
                            label="Lembrar minhas credenciais"
                            onChange={handleRememberCredentialsChange}
                            disabled={isLoadingVerify}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2, background: '#1c2e4a' }}
                            disabled={isLoadingVerify}
                        >
                            {isLoadingVerify ? (
                                <Skeleton variant="circular" width={24} height={24} />
                            ) : (
                                "ENTRAR"
                            )}

                        </Button>
                        {loginMessage.message &&
                            <Grid container sx={{paddingTop: 1, paddingBottom: 2}}>
                                <Grid item xs>
                                    <Alert severity={loginMessage.status}>{loginMessage.message}</Alert>
                                </Grid>
                            </Grid>
                        }
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}

export type {ICredentials}
export default LoginSide;